import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { History, Shield, RefreshCw, ChevronDown, ChevronUp, Clock, CheckCircle, XCircle, PauseCircle, AlertTriangle } from "lucide-react";
import { PasswordInput } from "@/components/ui/password-input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROCESS_NAMES = {
  process1: "Corrugation Machine",
  process2: "Sheet / Paper Cutting",
  process3: "Sheet Pasting",
  process4: "Thin Blade Slitter Scorer",
  process5: "4 Bar Rotary Creasing",
  process6: "RS4 Single Color Printing & Slotting",
  process6a: "Eccentric Slotter",
  process7: "Flat bed Punching",
  process8: "Rotary Die Punching",
  process9: "Calico Pasting",
  process10: "Stitching & Bundling",
  process11: "Loading / Unloading",
  process12: "Transport",
};

const STATUS_CONFIG = {
  completed: { label: "Completed", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", icon: CheckCircle },
  in_process: { label: "In Process", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30", icon: Clock },
  rejected: { label: "Rejected", color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", icon: XCircle },
  on_hold: { label: "On Hold", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", icon: PauseCircle },
};

const CompletionHistory = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWO, setExpandedWO] = useState(null);
  const [historyData, setHistoryData] = useState(null);

  // Modify dialog state
  const [showModifyDialog, setShowModifyDialog] = useState(false);
  const [modifyTarget, setModifyTarget] = useState(null);
  const [modifyPassword, setModifyPassword] = useState("");
  const [modifyStatus, setModifyStatus] = useState("");
  const [modifyEmployee, setModifyEmployee] = useState("");
  const [modifying, setModifying] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [woRes, empRes] = await Promise.all([
        axios.get(`${API}/work-orders`),
        axios.get(`${API}/employees`),
      ]);
      const wos = woRes.data.filter(wo => {
        const cp = wo.completed_processes || [];
        const processes = wo.processes || {};
        const selected = Object.keys(processes).filter(k => processes[k]);
        return selected.length > 0 || cp.length > 0;
      });
      setWorkOrders(wos);
      setEmployees(empRes.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (woNumber) => {
    try {
      const res = await axios.get(`${API}/work-orders/${woNumber}/completion-history`);
      setHistoryData(res.data);
    } catch (error) {
      toast.error("Failed to load history");
    }
  };

  const toggleExpand = async (woNumber) => {
    if (expandedWO === woNumber) {
      setExpandedWO(null);
      setHistoryData(null);
    } else {
      setExpandedWO(woNumber);
      await fetchHistory(woNumber);
    }
  };

  const openModifyDialog = (woNumber, processName, isCurrentlyCompleted) => {
    setModifyTarget({ woNumber, processName, isCurrentlyCompleted });
    setModifyPassword("");
    setModifyStatus("");
    setModifyEmployee("");
    setShowModifyDialog(true);
  };

  const handleModify = async () => {
    if (!modifyPassword || !modifyStatus) {
      toast.error("Please fill password and status");
      return;
    }
    if (modifyStatus === "completed" && !modifyEmployee) {
      toast.error("Please select the employee who completed this process");
      return;
    }
    setModifying(true);
    try {
      await axios.patch(`${API}/work-orders/${modifyTarget.woNumber}/modify-completion-history`, {
        password: modifyPassword,
        process_name: modifyTarget.processName,
        new_status: modifyStatus,
        employee_name: modifyStatus === "completed" ? modifyEmployee : null,
      });
      toast.success(`Process updated to ${STATUS_CONFIG[modifyStatus]?.label}`);
      setShowModifyDialog(false);
      await fetchHistory(modifyTarget.woNumber);
      await fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to modify";
      toast.error(msg);
    } finally {
      setModifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <RefreshCw className="h-10 w-10 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3" data-testid="completion-history-title">
            <History className="h-8 w-8 text-amber-400" />
            Completion History
          </h1>
          <p className="text-gray-400 mt-1">View and modify work order process completion status</p>
        </div>

        <div className="space-y-3">
          {workOrders.map((wo) => {
            const header = wo.header || {};
            const woNumber = header.work_order_number || "";
            const completed = wo.completed_processes || [];
            const processes = wo.processes || {};
            const selected = Object.keys(processes).filter(k => processes[k]);
            const isExpanded = expandedWO === woNumber;

            return (
              <Card key={wo.id} className="bg-black/60 border border-gray-700 overflow-hidden" data-testid={`wo-card-${woNumber}`}>
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/50 transition-colors"
                  onClick={() => toggleExpand(woNumber)}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-amber-400 font-bold text-lg">{woNumber}</span>
                    <span className="text-gray-400">{header.customer_name}</span>
                    <span className="text-gray-500 text-sm">{header.item_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className={`text-xs ${
                      wo.status === "completed" ? "border-emerald-500/50 text-emerald-400" :
                      wo.status === "in_progress" ? "border-blue-500/50 text-blue-400" :
                      "border-gray-500/50 text-gray-400"
                    }`}>
                      {isExpanded && historyData ? `${historyData.completed_processes?.length || 0}/${historyData.selected_processes?.length || 0}` : `${completed.length}/${selected.length}`} processes
                    </Badge>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>

                {isExpanded && historyData && (
                  <div className="border-t border-gray-700 p-4 space-y-4">
                    {/* Process Grid */}
                    <div className="space-y-2">
                      <Label className="text-gray-400 text-sm font-semibold">Process Status</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {historyData.selected_processes.sort((a, b) => {
                          const numA = parseInt(a.replace("process", ""));
                          const numB = parseInt(b.replace("process", ""));
                          return numA - numB;
                        }).map((proc) => {
                          const isCompleted = historyData.completed_processes.includes(proc);
                          const statusKey = isCompleted ? "completed" : "in_process";
                          const config = STATUS_CONFIG[statusKey];
                          const Icon = config.icon;
                          
                          // Find employee who completed this process
                          const historyEntry = historyData.completion_history?.find(h => h.process_name === proc);
                          const completedBy = historyEntry?.employee_name;

                          return (
                            <div
                              key={proc}
                              className={`flex items-center justify-between p-3 rounded-lg border ${config.bg}`}
                              data-testid={`process-row-${proc}`}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Icon className={`h-4 w-4 flex-shrink-0 ${config.color}`} />
                                <div className="min-w-0">
                                  <span className="text-white text-sm">{PROCESS_NAMES[proc] || proc}</span>
                                  {isCompleted && completedBy && (
                                    <div className="text-xs text-gray-400 truncate">by {completedBy}</div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`text-xs ${config.color} border-current`}>
                                  {config.label}
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openModifyDialog(woNumber, proc, isCompleted);
                                  }}
                                  data-testid={`modify-btn-${proc}`}
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  Modify
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Modification Log */}
                    {historyData.modification_log?.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-gray-400 text-sm font-semibold flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> Modification Log
                        </Label>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {historyData.modification_log.slice().reverse().map((log, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-xs p-2 bg-gray-800/50 rounded">
                              <span className="text-gray-500">{new Date(log.modified_at).toLocaleString()}</span>
                              <span className="text-white">{PROCESS_NAMES[log.process_name] || log.process_name}</span>
                              <span className="text-red-400">{log.from_status}</span>
                              <span className="text-gray-500">&rarr;</span>
                              <span className="text-emerald-400">{log.to_status}</span>
                              {log.employee_name && <span className="text-gray-400">by {log.employee_name}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}

          {workOrders.length === 0 && (
            <Card className="bg-black/60 border border-gray-700">
              <CardContent className="p-12 text-center">
                <History className="h-12 w-12 mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">No work orders with processes found</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modify Dialog */}
      <Dialog open={showModifyDialog} onOpenChange={setShowModifyDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <Shield className="h-5 w-5" />
              Modify Process Status
            </DialogTitle>
          </DialogHeader>
          {modifyTarget && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-800/50 rounded-lg text-sm">
                <p className="text-gray-400">Work Order: <span className="text-white font-bold">{modifyTarget.woNumber}</span></p>
                <p className="text-gray-400">Process: <span className="text-white font-bold">{PROCESS_NAMES[modifyTarget.processName] || modifyTarget.processName}</span></p>
                <p className="text-gray-400">Current: <span className={modifyTarget.isCurrentlyCompleted ? "text-emerald-400" : "text-blue-400"}>
                  {modifyTarget.isCurrentlyCompleted ? "Completed" : "In Process"}
                </span></p>
              </div>

              <div>
                <Label className="text-gray-400 text-sm">Password *</Label>
                <PasswordInput
                  value={modifyPassword}
                  onChange={(e) => setModifyPassword(e.target.value)}
                  placeholder="Enter authorization password"
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                  data-testid="modify-password-input"
                />
              </div>

              <div>
                <Label className="text-gray-400 text-sm">New Status *</Label>
                <Select value={modifyStatus} onValueChange={setModifyStatus}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="modify-status-select">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    {modifyTarget.isCurrentlyCompleted ? (
                      <>
                        <SelectItem value="in_process">In Process</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {modifyStatus === "completed" && (
                <div>
                  <Label className="text-gray-400 text-sm">Completed By *</Label>
                  <Select value={modifyEmployee} onValueChange={setModifyEmployee}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="modify-employee-select">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700 max-h-60">
                      {employees.map((emp) => (
                        <SelectItem key={emp.id || emp.name} value={emp.name}>
                          {emp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleModify}
                  disabled={modifying}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="modify-confirm-btn"
                >
                  {modifying ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                  Confirm Modification
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowModifyDialog(false)}
                  className="text-gray-400"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompletionHistory;
